import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { fmt2, fmtEUR } from '@/lib/utils';
import { 
  Search, Package, Edit, Trash2, Archive, Eye, 
  PlusCircle, Upload, MapPin, User, Filter
} from 'lucide-react';
import { getMySiteMaterials, deleteMaterial, archiveMaterial } from '@/lib/api';

const STATUS_COLORS = {
  available: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  documentation_missing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  reserved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  archived: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
  disposed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  mixed: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

export const MyMaterialsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteName, setSiteName] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const res = await getMySiteMaterials(params);
      setMaterials(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
      if (res.data.site) setSiteName(res.data.site.site_name);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('myMaterials.noSite') || 'Ihrem Konto ist kein Werk zugewiesen.');
      } else {
        toast.error(t('common.loadError') || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, t]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadMaterials();
  };

  const handleDelete = async () => {
    if (!selectedMaterial) return;
    try {
      await deleteMaterial(selectedMaterial.material_id);
      toast.success(t('myMaterials.deleted') || 'Material gelöscht');
      setDeleteDialogOpen(false);
      setSelectedMaterial(null);
      loadMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleArchive = async () => {
    if (!selectedMaterial) return;
    try {
      await archiveMaterial(selectedMaterial.material_id);
      toast.success(t('myMaterials.archived') || 'Material archiviert');
      setArchiveDialogOpen(false);
      setSelectedMaterial(null);
      loadMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Archivieren');
    }
  };

  if (!user?.site_id) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('myMaterials.noSiteTitle') || 'Kein Werk zugewiesen'}</h2>
          <p className="text-muted-foreground mb-6">
            {t('myMaterials.noSiteDesc') || 'Bitte wenden Sie sich an einen Administrator, um einem Werk zugewiesen zu werden.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="my-materials-page">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-manrope text-2xl font-bold" data-testid="page-title">
              {t('myMaterials.title') || 'Meine Werk-Artikel'}
            </h1>
            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">{siteName || '...'}</span>
              <span className="text-sm">({total} {t('myMaterials.articles') || 'Artikel'})</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" size="sm"
              onClick={() => navigate('/import')}
              data-testid="bulk-upload-btn"
            >
              <Upload className="h-4 w-4 mr-1" />
              {t('myMaterials.bulkUpload') || 'Massenupload'}
            </Button>
            <Button 
              size="sm" className="bg-orange-500 hover:bg-orange-600"
              onClick={() => navigate('/material/new')}
              data-testid="new-material-btn"
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              {t('myMaterials.newArticle') || 'Neuer Artikel'}
            </Button>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <Card className="mb-6" data-testid="filter-card">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder={t('myMaterials.searchPlaceholder') || 'Artikel suchen...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <Button type="submit" variant="outline" data-testid="search-btn">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('myMaterials.allStatuses') || 'Alle Status'}</SelectItem>
                  <SelectItem value="available">{t('status.available') || 'Verfügbar'}</SelectItem>
                  <SelectItem value="reserved">{t('status.reserved') || 'Reserviert'}</SelectItem>
                  <SelectItem value="documentation_missing">{t('status.documentation_missing') || 'Fehlende Dokumente'}</SelectItem>
                  <SelectItem value="archived">{t('myMaterials.statusArchived') || 'Archiviert'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Materials List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : materials.length === 0 ? (
          <Card className="py-16 text-center" data-testid="empty-state">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('myMaterials.empty') || 'Keine Artikel gefunden'}</h3>
            <p className="text-muted-foreground text-sm mb-6">
              {t('myMaterials.emptyDesc') || 'In Ihrem Werk wurden noch keine Artikel eingestellt.'}
            </p>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/material/new')}>
              <PlusCircle className="h-4 w-4 mr-2" />
              {t('myMaterials.newArticle') || 'Ersten Artikel einstellen'}
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="materials-list">
            {materials.map((m) => (
              <Card 
                key={m.material_id} 
                className={`transition-all hover:shadow-md ${m.status === 'archived' ? 'opacity-60' : ''}`}
                data-testid={`material-row-${m.material_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    {/* Left: Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 
                          className="font-medium text-sm truncate cursor-pointer hover:text-orange-500 transition-colors"
                          onClick={() => navigate(`/material/${m.material_id}`)}
                        >
                          {m.common_title}
                        </h3>
                        <Badge className={`text-xs shrink-0 ${STATUS_COLORS[m.status] || STATUS_COLORS.mixed}`}>
                          {m.status === 'available' ? (t('status.available') || 'Verfügbar') :
                           m.status === 'archived' ? (t('myMaterials.statusArchived') || 'Archiviert') :
                           m.status === 'documentation_missing' ? (t('status.documentation_missing') || 'Dok. fehlt') :
                           m.status === 'reserved' ? (t('status.reserved') || 'Reserviert') :
                           m.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>{m.manufacturer_name} - {m.manufacturer_part_number}</span>
                        {m.material_width_mm && <span>{m.material_width_mm}mm</span>}
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {m.is_own ? (t('myMaterials.you') || 'Sie') : m.owner_name}
                        </span>
                        <span>{m.sku_count} SKU(s)</span>
                      </div>
                    </div>

                    {/* Middle: Metrics */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-bold">{fmt2(m.total_quantity)}</p>
                        <p className="text-xs text-muted-foreground">{t('myMaterials.quantity') || 'Menge'}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-orange-500">{fmtEUR(m.total_value_kEUR)} EUR</p>
                        <p className="text-xs text-muted-foreground">{t('myMaterials.value') || 'Wert'}</p>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => navigate(`/material/${m.material_id}`)}
                        title={t('common.view') || 'Ansehen'}
                        data-testid={`view-btn-${m.material_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => navigate(`/material/${m.material_id}/edit`)}
                        title={t('common.edit') || 'Bearbeiten'}
                        data-testid={`edit-btn-${m.material_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {m.status !== 'archived' && (
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => { setSelectedMaterial(m); setArchiveDialogOpen(true); }}
                          title={t('myMaterials.archive') || 'Archivieren'}
                          data-testid={`archive-btn-${m.material_id}`}
                        >
                          <Archive className="h-4 w-4 text-amber-500" />
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => { setSelectedMaterial(m); setDeleteDialogOpen(true); }}
                        title={t('common.delete') || 'Löschen'}
                        data-testid={`delete-btn-${m.material_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-6" data-testid="pagination">
            <Button 
              variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              {t('common.previous') || 'Zurück'}
            </Button>
            <span className="flex items-center text-sm text-muted-foreground px-3">
              {page} / {pages}
            </span>
            <Button 
              variant="outline" size="sm" disabled={page >= pages}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next') || 'Weiter'}
            </Button>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('myMaterials.deleteTitle') || 'Material löschen?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('myMaterials.deleteDesc') || 'Das Material und alle zugehörigen Bestände werden unwiderruflich gelöscht.'}
              <br />
              <strong>{selectedMaterial?.common_title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">{t('common.cancel') || 'Abbrechen'}</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
            >
              {t('common.delete') || 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent data-testid="archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('myMaterials.archiveTitle') || 'Material archivieren?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('myMaterials.archiveDesc') || 'Das Material wird archiviert und ist nicht mehr im Marktplatz sichtbar.'}
              <br />
              <strong>{selectedMaterial?.common_title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel-btn">{t('common.cancel') || 'Abbrechen'}</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleArchive}
              data-testid="archive-confirm-btn"
            >
              {t('myMaterials.archive') || 'Archivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
